import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import { service } from '@ember/service';

import TerraformUnificationService from 'core/services/terraform-unification';
import { ResourceType } from 'core/services/terraform-unification';

import {
  HdsAlert,
  HdsAlertColorValues,
  HdsAlertTypeValues,
} from '@hashicorp/design-system-components/components';
import {
  HdsButtonColorValues,
  HdsButtonIconPositionValues,
} from '@hashicorp/design-system-components/components/hds/button/types';
import {
  HdsLinkColorValues,
  HdsLinkIconPositionValues,
} from '@hashicorp/design-system-components/components/hds/link/types';

import type {
  HashicorpCloudResourcemanagerOrganization,
  HashicorpCloudResourcemanagerProject,
} from '@clients/cloud-resource-manager';
import type { HashicorpCloudIamGroup } from '@clients/cloud-iam';
import type { OnClickManageTerraformPermissionsFunction } from 'manage-access/types/manage-terraform-permissions';

export interface ManageAccessAlertManageTerraformPermissionsSignature {
  Args: {
    group?: HashicorpCloudIamGroup;
    onClickManageTerraformPermissions?: OnClickManageTerraformPermissionsFunction;
    organization: HashicorpCloudResourcemanagerOrganization;
    project?: HashicorpCloudResourcemanagerProject;
  };
  Element: HTMLElement;
}

export default class ManageAccessAlertManageTerraformPermissionsComponent extends Component<ManageAccessAlertManageTerraformPermissionsSignature> {
  @service('terraform-unification')
  declare readonly terraformUnification: TerraformUnificationService;

  get terraformRoleAssignmentsUrl() {
    const { organization, project, group } = this.args;
    const url = this.terraformUnification.getDeploymentUrl({});

    if (!url || !organization?.name) {
      return undefined;
    }

    if (!group?.resourceId) {
      return this.terraformUnification.getResourceUrl({
        url,
        resourceType: project?.id
          ? ResourceType.TerraformProjectRoleAssignments
          : ResourceType.TerraformOrganizationRoleAssignments,
        resourceIdentifiers: {
          terraformOrganizationName: organization.name,
          projectId: project?.id,
        },
      });
    }

    return this.terraformUnification.getResourceUrl({
      url,
      resourceType: project?.id
        ? ResourceType.TerraformProjectTeamRoleAssignments
        : ResourceType.TerraformOrganizationTeamRoleAssignments,
      resourceIdentifiers: {
        terraformOrganizationName: organization.name,
        projectId: project?.id,
        resourceId: group.resourceId,
      },
    });
  }

  handleOnClickManageTerraformPermissions = () => {
    const { onClickManageTerraformPermissions } = this.args;
    const url = this.terraformRoleAssignmentsUrl;

    if (
      onClickManageTerraformPermissions &&
      typeof onClickManageTerraformPermissions === 'function' &&
      typeof url === 'string'
    ) {
      onClickManageTerraformPermissions({ url });
    }
  };

  <template>
    <HdsAlert
      @color={{HdsAlertColorValues.Neutral}}
      @icon="terraform"
      @type={{HdsAlertTypeValues.Inline}}
      data-test-manage-access-manage-terraform-permissions
      ...attributes
      as |A|
    >
      <A.Title>
        {{t
          "manage-access.components.alert.manage-terraform-permissions.looking-for-terraform-permissions"
        }}
      </A.Title>
      <A.Description>
        {{t
          "manage-access.components.alert.manage-terraform-permissions.manage-terraform-specific-permissions"
        }}
      </A.Description>
      <A.Button
        @color={{HdsButtonColorValues.Secondary}}
        @href={{if
          @onClickManageTerraformPermissions
          undefined
          this.terraformRoleAssignmentsUrl
        }}
        @icon="external-link"
        @iconPosition={{HdsButtonIconPositionValues.Trailing}}
        @text={{t
          "manage-access.components.alert.manage-terraform-permissions.manage-terraform-permissions"
        }}
        {{on "click" this.handleOnClickManageTerraformPermissions}}
        data-test-manage-access-manage-terraform-permissions-manage-button
      />
      <A.LinkStandalone
        @color={{HdsLinkColorValues.Secondary}}
        @icon="docs-link"
        @iconPosition={{HdsLinkIconPositionValues.Leading}}
        @text={{t
          "manage-access.components.alert.manage-terraform-permissions.learn-more-about-terraform-permissions"
        }}
        @href="https://developer.hashicorp.com/terraform/cloud-docs/users-teams-organizations/permissions"
        data-test-manage-access-manage-terraform-permissions-documentation-link
      />
    </HdsAlert>
  </template>
}
